import { type ReactNode, useContext } from "react";

import {
  Avatar,
  Box,
  CodeIcon,
  Column,
  RefreshIcon,
  useDisclosure,
} from "@hightouch/htui";
import { noop } from "lodash";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Skeleton } from "src/components/loading";
import {
  UserMenu,
  UserMenuDivider,
  UserMenuItem,
} from "src/components/layout/header/user-menu";
import { CompactNavContext } from "src/components/layout/nav/compact-nav-context";
import { NavItem } from "src/components/layout/nav/nav-item";
import { useUser } from "src/contexts/user-context";
import { More } from "src/components/layout/nav/more";
import { Icon as ContextIcon } from "src/components/layout/nav/icons/context";
import { Icon as SettingsIcon } from "src/components/layout/nav/icons/settings";
import type { ActiveSectionProps } from "src/components/layout/nav/utils";
import { Settings } from "src/components/layout/nav/sections/settings";
import { navIcon } from "src/components/layout/nav/nav-icon";
import { useContextHomePath } from "src/components/layout/nav/sections/hooks/context";
import { useHasOrganizationSettings } from "src/hooks/use-has-organization-settings";
import { NavItemText } from "src/components/layout/nav/nav-item-text";
import { useSpaceContext } from "src/components/spaces";
import { useSettingsHomePath } from "src/components/layout/nav/sections/hooks/settings";
import {
  isContextPath,
  productForPath,
  useCanUseProductTabs,
  useHasProductTabs,
} from "src/components/layout/nav/sections/hooks/projects";
import { useLocation } from "src/router";
import { useClassicNavPreference } from "src/components/layout/nav/use-classic-nav";
import { useCanUseDevSettings } from "src/pages/agents/hooks/use-can-use-dev-settings";
import { useDevSettings } from "src/pages/agents/hooks/use-dev-settings";
import { ChatSupport } from "./chat-support";
import { DevTools } from "./dev-tools";
import { PoweredBy } from "./powered-by";
import { useResourceLinks } from "./resource-links";

export const ContextNavIcon = navIcon(ContextIcon);

type FooterProps = {
  /** The nav's settings entries, above the user menu. */
  children: ReactNode;
};

export const Footer = ({ children }: FooterProps) => {
  const { user } = useUser();
  const { organizationLogoCobrandingHidden } = useFlags();
  const isCompact = useContext(CompactNavContext);
  const userMenu = useDisclosure();
  const canUseProductTabs = useCanUseProductTabs();
  const hasProductTabs = useHasProductTabs();
  const { isClassicNav, setIsClassicNav } = useClassicNavPreference();
  const canUseDevSettings = useCanUseDevSettings();
  const { settings: devSettings, setSettings: setDevSettings } =
    useDevSettings();
  const resourceLinks = useResourceLinks();
  const showPoweredBy =
    user?.organization?.logo_url && !organizationLogoCobrandingHidden;

  return (
    // With the AMP nav list's 8px bottom padding and the nav's 8px gap, 24px under its last row, a step past the 16px before.
    <Column mt={2} px={2}>
      <More />

      <Column gap={isCompact ? 2 : 3}>
        {/* Pylon only loads in non-dev builds, so Help chat has no function
            in dev — hide it there (dev shows the Dev tools item instead). */}
        {!import.meta.env.DEV && !hasProductTabs && <ChatSupport />}

        {children}

        {/* Beside the product nav, Help chat sits right above the user menu, after the workspace's own rows. */}
        {!import.meta.env.DEV && hasProductTabs && <ChatSupport />}

        {import.meta.env.DEV && <DevTools />}

        {user ? (
          <UserMenu
            user={user}
            placement="right-start"
            offset={[0, 16]}
            isOpen={userMenu.isOpen}
            onClose={userMenu.onClose}
            items={
              <>
                {canUseProductTabs && (
                  <UserMenuItem
                    icon={RefreshIcon}
                    label={
                      isClassicNav
                        ? "Use new navigation"
                        : "Use classic navigation"
                    }
                    onClick={() => {
                      setIsClassicNav(!isClassicNav);
                      userMenu.onClose();
                    }}
                  />
                )}
                {canUseDevSettings && (
                  <UserMenuItem
                    icon={CodeIcon}
                    label="Dev mode"
                    isChecked={devSettings.isDevMode}
                    // Leaves the menu open so the switch is seen to flip.
                    onClick={() =>
                      setDevSettings({ isDevMode: !devSettings.isDevMode })
                    }
                  />
                )}
                {(canUseProductTabs || canUseDevSettings) && (
                  <UserMenuDivider />
                )}
                {/* The nav's Resources, here so the footer stays short. */}
                {resourceLinks.map(({ onClick, ...link }) => (
                  <UserMenuItem
                    key={link.label}
                    {...link}
                    onClick={() => {
                      onClick();
                      userMenu.onClose();
                    }}
                  />
                ))}
              </>
            }
          >
            <NavItem
              isActive={userMenu.isOpen}
              hasCollapsedTooltip={false}
              onClick={userMenu.onToggle}
            >
              <Box
                as={Avatar}
                size="sm"
                flexShrink={0}
                name={user.name || "unknown user"}
              />
              <NavItemText>
                {user.email === "guest" ? user.name : user.email}
              </NavItemText>
            </NavItem>
          </UserMenu>
        ) : (
          <NavItem hasCollapsedTooltip={false} onClick={noop}>
            <Skeleton height={32} width={32} circle style={{ flexShrink: 0 }} />
            <Skeleton height={14} width={100} />
          </NavItem>
        )}

        {/* The AMP nav's header carries the workspace's mark, so Hightouch's moves down here. */}
        {(showPoweredBy || isCompact) && <PoweredBy />}
      </Column>
    </Column>
  );
};

/** The classic nav's Settings: a section of links for organization admins, otherwise one link. */
export const ClassicSettingsLinks = (props: ActiveSectionProps) => {
  const { hasAccess: hasOrgSettings } = useHasOrganizationSettings();
  const { isInSpace, isPathAccessibleInSpace } = useSpaceContext();

  if (isInSpace && !isPathAccessibleInSpace("/settings")) {
    return null;
  }
  return hasOrgSettings ? (
    <Settings {...props} />
  ) : (
    <NavItem href="/settings" icon={SettingsIcon} label="Settings" />
  );
};

/** The AMP nav's Context and Settings, which open the full-screen settings. */
export const AmpSettingsLinks = () => {
  const settingsHomePath = useSettingsHomePath();
  const contextHomePath = useContextHomePath();
  const { pathname } = useLocation();
  // Their pages sit deeper than the one-segment match a nav link makes, so they light up by product instead.
  const isOnContext = isContextPath(pathname);
  const isOnSettings = productForPath(pathname) === "settings" && !isOnContext;

  return (
    <>
      {contextHomePath && (
        <NavItem
          isActive={isOnContext}
          href={contextHomePath}
          icon={ContextNavIcon}
          label="Context"
        />
      )}
      {settingsHomePath && (
        <NavItem
          isActive={isOnSettings}
          href={settingsHomePath}
          icon={SettingsIcon}
          label="Settings"
        />
      )}
    </>
  );
};
